import { z } from 'zod';
import { defineEndpoint } from '../contract.ts';
import { CommandStatus } from '../discord/protocol.ts';
import { PageQuery, paginated } from '../pagination.ts';
import { timestamp } from '../schemas.ts';

// The system page's API (design §6.11): parked events, jobs, Discord workers and commands.

export const EventFailureStatus = z.enum(['parked', 'replayed', 'discarded']);
export type EventFailureStatus = z.infer<typeof EventFailureStatus>;

export const EventFailure = z.object({
  id: z.string(),
  /** `<module>:<name>`. */
  subscriber: z.string(),
  /** Null once event retention has removed the event. */
  eventId: z.string().nullable(),
  seq: z.number().int(),
  type: z.string(),
  attempts: z.number().int(),
  error: z.string(),
  status: EventFailureStatus,
  /** The core_users id of the admin who replayed or discarded it. */
  resolvedBy: z.string().nullable(),
  resolvedAt: timestamp.nullable(),
  created: timestamp,
});
export type EventFailure = z.infer<typeof EventFailure>;

export const listEventFailures = defineEndpoint({
  method: 'GET',
  path: '/admin/system/events/failures',
  permission: 'core.system.view',
  query: z.object({ status: EventFailureStatus.default('parked') }),
  response: z.object({ items: z.array(EventFailure) }),
});

const FailureParams = z.object({ id: z.string().regex(/^[a-z0-9]{15}$/) });

export const replayEventFailure = defineEndpoint({
  method: 'POST',
  path: '/admin/system/events/failures/:id/replay',
  permission: 'core.system.manage',
  recentLogin: true,
  params: FailureParams,
  response: z.object({ failure: EventFailure }),
});

export const discardEventFailure = defineEndpoint({
  method: 'POST',
  path: '/admin/system/events/failures/:id/discard',
  permission: 'core.system.manage',
  recentLogin: true,
  params: FailureParams,
  response: z.object({ failure: EventFailure }),
});

export const JobStatus = z.object({
  /** `<module>:<job>`. */
  name: z.string(),
  /** `every 15m`, or `cron 0 4 * * * (UTC)`. */
  schedule: z.string(),
  running: z.boolean(),
  lastStartedAt: timestamp.nullable(),
  lastFinishedAt: timestamp.nullable(),
  nextRunAt: timestamp.nullable(),
  /** Empty after a successful run. */
  lastError: z.string(),
  lastDurationMs: z.number().int(),
});
export type JobStatus = z.infer<typeof JobStatus>;

export const listJobs = defineEndpoint({
  method: 'GET',
  path: '/admin/system/jobs',
  permission: 'core.system.view',
  response: z.object({ items: z.array(JobStatus) }),
});

export const WorkerSession = z.object({
  id: z.string(),
  workerId: z.string(),
  version: z.string(),
  capabilities: z.array(z.string()),
  /** When it said hello. */
  connectedAt: timestamp,
  /** Whether its stream is open. A session that said hello but never opened one is dropped after 30 s. */
  streaming: z.boolean(),
});
export type WorkerSession = z.infer<typeof WorkerSession>;

/** The last reconcile of core_users against a guild snapshot. */
export const ReconcileStatus = z.object({
  at: timestamp,
  trigger: z.enum(['hello', 'gateway_ready', 'buffer_overflowed', 'daily', 'admin']),
  /** Member events published. */
  changed: z.number().int(),
  /** Why the safety guard stopped it, with nothing changed. */
  aborted: z.string().nullable(),
  /** Why the snapshot or publishing failed, with nothing changed. */
  error: z.string().nullable(),
});
export type ReconcileStatus = z.infer<typeof ReconcileStatus>;

export const listWorkers = defineEndpoint({
  method: 'GET',
  path: '/admin/system/workers',
  permission: 'core.system.view',
  response: z.object({
    /** False when the server has no ITC_WORKER_TOKEN, so no worker can connect. */
    configured: z.boolean(),
    sessions: z.array(WorkerSession),
    /** Null until the first reconcile since the server started. */
    reconcile: ReconcileStatus.nullable(),
  }),
});

/** Reconciles core_users against a guild snapshot now, and answers with the result. */
export const reconcileDiscord = defineEndpoint({
  method: 'POST',
  path: '/admin/system/discord/reconcile',
  permission: 'core.system.manage',
  recentLogin: true,
  response: z.object({ reconcile: ReconcileStatus }),
});

export const Command = z.object({
  id: z.string(),
  capability: z.string(),
  module: z.string(),
  /** `user:<id>`, `discord:<discord id>`, `service:<module>` or `system`. */
  actor: z.string(),
  actorUser: z.string().nullable(),
  requestId: z.string(),
  key: z.string().nullable(),
  /** What it was asked to do, as staged. */
  payload: z.unknown(),
  status: CommandStatus,
  attempts: z.number().int(),
  deadline: timestamp,
  error: z.string(),
  claimedAt: timestamp.nullable(),
  finishedAt: timestamp.nullable(),
  created: timestamp,
  updated: timestamp,
});
export type Command = z.infer<typeof Command>;

export const listCommands = defineEndpoint({
  method: 'GET',
  path: '/admin/system/commands',
  permission: 'core.system.view',
  query: PageQuery.extend({ status: CommandStatus.optional() }),
  response: paginated(Command),
});

const CommandParams = z.object({ id: z.string().regex(/^[a-z0-9]{15}$/) });

/** Puts an `unknown` or `failed` command back to `pending`, with a fresh deadline. */
export const retryCommand = defineEndpoint({
  method: 'POST',
  path: '/admin/system/commands/:id/retry',
  permission: 'core.system.manage',
  recentLogin: true,
  params: CommandParams,
  response: z.object({ command: Command }),
});

/** Gives up on an `unknown` or `pending` command: it ends `failed`, and is never sent. */
export const discardCommand = defineEndpoint({
  method: 'POST',
  path: '/admin/system/commands/:id/discard',
  permission: 'core.system.manage',
  recentLogin: true,
  params: CommandParams,
  response: z.object({ command: Command }),
});

/** Everything the system page's overview shows (plan 1C Chunk 8), read on each request. */
export const SystemOverview = z.object({
  /** The deployed git sha, or "dev". */
  sha: z.string(),
  uptime: z.number(),
  eventLoopDelay: z.object({ p50: z.number(), p99: z.number() }),
  pocketbase: z.enum(['ok', 'error']),
  worker: z.object({ configured: z.boolean(), sessions: z.array(WorkerSession) }),
  /** How far each subscriber is behind the newest event. */
  lag: z.array(z.object({ subscriber: z.string(), behind: z.number().int() })),
  parked: z.number().int(),
  commands: z.object({
    counts: z.record(CommandStatus, z.number().int()),
    /** How long the oldest pending command has waited, or null for none. */
    oldestPendingMs: z.number().int().nullable(),
  }),
  /** Every capability and whether it's safe to run twice, so a retry can warn. */
  capabilities: z.array(z.object({ name: z.string(), idempotent: z.boolean() })),
  reconcile: ReconcileStatus.nullable(),
  /** Role links (plan 5C): how many, how many are on, and how many of those are held back. The detail is on /admin/role-links. */
  roleLinks: z.object({ total: z.number().int(), enabled: z.number().int(), blocked: z.number().int() }),
});
export type SystemOverview = z.infer<typeof SystemOverview>;

export const getSystemOverview = defineEndpoint({
  method: 'GET',
  path: '/admin/system/overview',
  permission: 'core.system.view',
  response: SystemOverview,
});
